/**
 * Edit capture.
 *
 * Every edit flow reports itself via `clipVersionStore.emitEdit`. This module is the
 * subscriber: it records what the user did, per clip, in the user's terms ("Applied template")
 * rather than by the internal actions it fanned out into.
 *
 * Version *creation* is the backend's, triggered by the `first_updated_at_after_last_version`
 * field this module writes on the first edit of a window and notified back over Pusher.
 * `reconcileEditLogAfterVersion` closes that loop: the backend snapshots the clip row as it
 * stood when its job fired, so edits can outrun the version, and reconciling keeps those
 * pending instead of dropping them.
 */

import type { ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import type { EditLogEntry, PendingLabel } from './types';
import VERSIONS_DESCRIPTION_MAP from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/versionsDescriptionMap';
import { clipVersionStore, type HistoryStepDirection } from '@/stores/clipVersionStore';
import { currentClip, saveClipChanges, updateClipMetadataWithId } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const editLogs: Record<string, EditLogEntry[]> = {};

/** Edits taken back by undo, newest last, so redo can put their labels back. */
const undoneEditLogs: Record<string, EditLogEntry[]> = {};

/** Edits under this category mutate the broadcast's chapters rather than the clip itself. */
const CHAPTERS_CATEGORY = 'chapters';

/** Window within which a repeat of the same edit type is treated as the same action. */
const DUPLICATE_EDIT_WINDOW_MS = 500;
const lastLoggedEdit: Record<string, { updateType: string; at: number }> = {};

function isVersioningEnabled(): boolean {
  return !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Clip_Versioning];
}

/**
 * Writes versioning's own bookkeeping fields into `asset_metadata`, from where the regular
 * clip-save path carries them to the backend.
 *
 * Always skips the player-config rebuild: none of these fields feed it, and this runs on
 * every captured edit.
 */
function patchVersioningMetadata(clipId: string, patch: Partial<ClipMetadata>): void {
  updateClipMetadataWithId(clipId, patch, false);
}

function getPendingLabels(clipId: string): PendingLabel[] {
  return currentClip.getSnapshot()[clipId]?.asset_metadata.pending_action_labels ?? [];
}

function toLabel({ category, label }: EditLogEntry): PendingLabel {
  return { category, label };
}

function appendPendingLabel(clipId: string, entry: EditLogEntry): void {
  patchVersioningMetadata(clipId, { pending_action_labels: [...getPendingLabels(clipId), toLabel(entry)] });
}

/**
 * Subscriber for `clipVersionStore.emitEdit`, registered by this module's index.
 * Records the edit against the clip and arms the backend's version trigger.
 */
export function logClipEdit(clipId: string, updateType: string): void {
  // emitEdit itself is deliberately ungated — the seam always reports edits and the consumer
  // decides — but without this check every user would accumulate edits, and produce versions,
  // regardless of rollout state.
  if (!isVersioningEnabled()) {
    return;
  }

  // While previewing a version the clip holds that version's state, not the user's work —
  // capturing it would save the previewed version back as new work. The dispatchers carry the
  // same guard, so this is the second line of defence for anything that emits without them.
  if (clipVersionStore.isPreviewing(clipId)) {
    return;
  }

  // A single gesture can report the same edit more than once — React invokes state updaters
  // twice under StrictMode in dev, and some handlers fire on both a move and its release.
  // Collapse repeats of the same type in quick succession so one action records once.
  const now = Date.now();
  const previous = lastLoggedEdit[clipId];
  if (previous && previous.updateType === updateType && now - previous.at < DUPLICATE_EDIT_WINDOW_MS) {
    return;
  }
  lastLoggedEdit[clipId] = { updateType, at: now };

  const mapped = VERSIONS_DESCRIPTION_MAP[updateType];
  const entry: EditLogEntry = {
    timestamp: new Date().toISOString(),
    updateType,
    category: mapped?.category ?? 'unknown',
    label: mapped?.label ?? updateType
  };

  const isFirstEditInWindow = !editLogs[clipId] || editLogs[clipId].length === 0;

  (editLogs[clipId] ??= []).push(entry);

  // A fresh edit forks away from the undone branch, as the history does with its redo states.
  delete undoneEditLogs[clipId];

  // The backend starts its version timer from this field, so it is written once per window.
  if (isFirstEditInWindow) {
    patchVersioningMetadata(clipId, { first_updated_at_after_last_version: entry.timestamp });
  }

  // Deduplication by category is the backend's responsibility. Labels ride along with the
  // next regular clip save.
  appendPendingLabel(clipId, entry);

  // Chapter edits write to the broadcast instead of the clip, so no clip save follows to carry
  // the two fields above to the backend — without this the version is never triggered.
  if (entry.category === CHAPTERS_CATEGORY) {
    saveClipChanges({
      clipId,
      shouldAddToHistory: false,
      shouldGenerateThumbnail: false,
      isEditedState: currentClip.getSnapshot()[clipId]?.asset_metadata.is_edited
    });
  }
}

/**
 * Drops an undone edit's label from the pending description, and restores it on redo, so a
 * trim the user took back stops reading as "Trimmed clip".
 *
 * One step consumes one logged edit, which assumes the log and the undo history advanced
 * together. A repeat collapsed by the dedup window logs once but may still add its own history
 * state, so a step can consume an older entry than the one undone. Both lists still empty out
 * together and the backend dedupes by label, so the description stays plausible either way.
 */
export function stepEditLog(clipId: string, direction: HistoryStepDirection): void {
  // Undo/redo fires for everyone; only act on it where versioning is capturing edits.
  if (!isVersioningEnabled()) {
    return;
  }

  if (direction === 'redo') {
    const entry = undoneEditLogs[clipId]?.pop();
    if (!entry) {
      return;
    }
    (editLogs[clipId] ??= []).push(entry);
    appendPendingLabel(clipId, entry);
    return;
  }

  const entry = editLogs[clipId]?.pop();
  if (!entry) {
    return;
  }
  (undoneEditLogs[clipId] ??= []).push(entry);

  const labels = getPendingLabels(clipId);
  let index = -1;
  for (let i = labels.length - 1; i >= 0; i -= 1) {
    if (labels[i].category === entry.category && labels[i].label === entry.label) {
      index = i;
      break;
    }
  }
  if (index === -1) {
    return;
  }

  const remainingLabels = [...labels.slice(0, index), ...labels.slice(index + 1)];
  const logIsNowEmpty = (editLogs[clipId]?.length ?? 0) === 0;

  patchVersioningMetadata(clipId, {
    pending_action_labels: remainingLabels,
    // Once every edit is undone there is nothing for the backend to version. Clear the anchor
    // so it doesn't create a version for work the user took back entirely.
    ...(logIsNowEmpty ? { first_updated_at_after_last_version: null } : {})
  });
}

/** Pending (not-yet-versioned) edits for a clip, oldest first. */
export function getEditLog(clipId: string): EditLogEntry[] {
  return editLogs[clipId] ?? [];
}

/**
 * Reconciles the local edit log against a version the backend has just created.
 *
 * The backend builds a version from the clip row as it stood when its job fired, which can lag
 * the editor — edits made between that read and this event arriving are not in the version.
 * Splitting the log on the version's timestamp keeps those edits pending rather than discarding
 * them, so they are described by, and trigger, the next version.
 *
 * Re-arming `first_updated_at_after_last_version` is the critical part: the backend starts its
 * timer from that field, so nulling it while edits are still pending would leave them with no
 * trigger and no further version would ever be created for them.
 *
 * Returns true when edits survived — meaning the new version does not represent what the editor
 * currently shows.
 */
export function reconcileEditLogAfterVersion(clipId: string, versionCreatedAt: string): boolean {
  const pending = editLogs[clipId] ?? [];
  const cutoff = Date.parse(versionCreatedAt);

  // An unparseable timestamp leaves every entry pending. That can repeat a label on the next
  // version, which is recoverable; assuming the version captured them is not, since those edits
  // would lose both their description and their trigger.
  const survivors = Number.isNaN(cutoff) ? pending : pending.filter(entry => Date.parse(entry.timestamp) > cutoff);

  editLogs[clipId] = survivors;
  // The version closes this window, and undo history resets with it.
  delete undoneEditLogs[clipId];
  patchVersioningMetadata(clipId, {
    first_updated_at_after_last_version: survivors[0]?.timestamp ?? null,
    pending_action_labels: survivors.map(({ category, label }) => ({ category, label }))
  });

  return survivors.length > 0;
}

/**
 * Discards a clip's pending edits.
 *
 * The metadata fields are cleared in the local store only; no save is issued, so a version
 * window the backend has already been told about still runs to completion there.
 */
export function clearEditLog(clipId: string): void {
  editLogs[clipId] = [];
  delete undoneEditLogs[clipId];
  // Reset too, so the first edit after leaving a preview or finishing a restore is never
  // mistaken for a duplicate of whatever was logged before it.
  delete lastLoggedEdit[clipId];
  patchVersioningMetadata(clipId, { pending_action_labels: [], first_updated_at_after_last_version: null });
}

// Self-register so edits are captured as long as this module is loaded, whichever entry point
// pulled it in — the barrel, or the controller behind the versioning UI.
clipVersionStore.onEdit(logClipEdit);
clipVersionStore.onHistoryStep(stepEditLog);
